/**
 * features/admin/TermDatesSettings.tsx
 * ------------------------------------------------------------------
 * School Settings → Term Dates. Admin-editable start / end / reopen date
 * per term, stored at reportSchoolSettings/termCalendar. This is the single
 * source of truth that feeds report cards (term-date line + "school
 * reopens") and anywhere term dates are shown; unset terms fall back to
 * lib/school.config. See lib/termDates.ts.
 * ------------------------------------------------------------------ */
import { useEffect, useMemo, useState } from 'react';
import { doc } from 'firebase/firestore';
import { CalendarDays, Save, CheckCircle2, AlertCircle } from 'lucide-react';
import { db, setDoc } from '@/lib/firebase';
import { useData, COLLECTIONS } from '@/contexts/DataContext';
import { useAuth } from '@/contexts/AuthContext';
import { SCHOOL, type TermKey } from '@/lib/school.config';
import { formatDateRange, formatLongDate, TERM_DATE_DEFAULTS as DEFAULTS } from '@/lib/termDates';
import { GradientHero, SoftCard, AccentButton, glassFieldClass } from '@/components/ui/glass';
import { Spinner } from '@/components/ui/primitives';
import type { TermCalendarEntry } from '@/types';

export default function TermDatesSettings({ year }: { year: string }) {
  const { termCalendar } = useData();
  const { userData } = useAuth();
  const termKeys = useMemo(() => Object.keys(SCHOOL.terms) as TermKey[], []);

  const [entries, setEntries] = useState<Record<string, TermCalendarEntry>>({});
  const [ready, setReady] = useState(false);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);

  useEffect(() => {
    const seed: Record<string, TermCalendarEntry> = {};
    for (const t of termKeys) {
      seed[t] = termCalendar?.terms?.[t] ?? DEFAULTS[t] ?? { start: '', end: '', reopens: '' };
    }
    setEntries(seed);
    setReady(true);
  }, [termCalendar, termKeys]);

  function set(term: string, field: keyof TermCalendarEntry, value: string) {
    setEntries((e) => ({ ...e, [term]: { ...e[term], [field]: value } }));
    setMsg(null);
  }

  async function save() {
    setSaving(true);
    setMsg(null);
    try {
      await setDoc(
        doc(db, COLLECTIONS.schoolSettings, 'termCalendar'),
        { id: 'termCalendar', year, terms: entries, updatedAt: new Date().toISOString(), updatedBy: userData?.uid ?? '' },
        { merge: true },
      );
      setMsg({ ok: true, text: 'Term dates saved — reports now use these.' });
    } catch (e) {
      setMsg({ ok: false, text: (e as Error).message });
    } finally {
      setSaving(false);
    }
  }

  if (!ready) return <div className="flex justify-center py-16"><Spinner label="Loading…" /></div>;

  return (
    <div className="space-y-6">
      <GradientHero
        icon={CalendarDays}
        title="Term Dates"
        subtitle="Start, end and reopen dates per term — feeds report cards and anywhere term dates appear"
        actions={<AccentButton icon={Save} onClick={save} disabled={saving}>{saving ? 'Saving…' : 'Save'}</AccentButton>}
      />

      {msg && (
        <SoftCard>
          <div className={`flex items-center gap-2 text-sm ${msg.ok ? 'text-emerald-700 dark:text-emerald-300' : 'text-red-700 dark:text-red-300'}`}>
            {msg.ok ? <CheckCircle2 size={16} /> : <AlertCircle size={16} />}{msg.text}
          </div>
        </SoftCard>
      )}

      <div className="grid gap-4 md:grid-cols-3">
        {termKeys.map((t) => {
          const e = entries[t] ?? { start: '', end: '', reopens: '' };
          return (
            <SoftCard key={t}>
              <h3 className="font-semibold text-gray-900 dark:text-white mb-3">{SCHOOL.terms[t].label}</h3>
              <div className="space-y-3">
                <div>
                  <label className="text-xs font-medium text-gray-500">Term starts</label>
                  <input type="date" className={glassFieldClass('mt-1')} value={e.start} onChange={(ev) => set(t, 'start', ev.target.value)} />
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-500">Term ends</label>
                  <input type="date" className={glassFieldClass('mt-1')} value={e.end} onChange={(ev) => set(t, 'end', ev.target.value)} />
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-500">School reopens (next term)</label>
                  <input type="date" className={glassFieldClass('mt-1')} value={e.reopens} onChange={(ev) => set(t, 'reopens', ev.target.value)} />
                </div>
              </div>
              <div className="mt-3 pt-3 border-t border-gray-100 dark:border-gray-700 text-xs text-gray-500 space-y-0.5">
                <p><span className="text-gray-400">On report:</span> {formatDateRange(e.start, e.end) || '—'}</p>
                <p><span className="text-gray-400">Reopens:</span> {formatLongDate(e.reopens) || '—'}</p>
              </div>
            </SoftCard>
          );
        })}
      </div>

      <p className="text-xs text-gray-400">
        These dates feed the report card's term-date line and "School reopens" date. A term left unset falls back to the
        built-in calendar so nothing is ever blank.
      </p>
    </div>
  );
}
